import { ICexAccount, ICexAccountShort } from 'types/accounts';

import { ICexNotConnectedAccounts } from './models';

import { axiosToken } from '../apiBase/axios';
import type { IBaseResponse } from '../apiBase/IBaseResponse';
import { mapError } from '../apiBase/mapError';
import { mapResponse } from '../apiBase/mapResponse';

class ApiCexAccounts {
  /**
   * @deprecated Use `getAccounts` from `entities/cex-account/api` or
   * `useGetAccounts` query hook instead for AbortController support.
   * @see https://api.dev.111pg.info/swagger/#/cex-pairs/CexPairsAccountsListRequest
   */
  public static getAccounts = async ({
    limit,
    lastSeenId,
    pairId,
  }: {
    limit: number;
    lastSeenId?: number;
    pairId: number;
  }) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append('limit', limit.toString());
      lastSeenId && urlParams.append('last_seen_id', lastSeenId.toString());

      const response = await axiosToken.get<
        IBaseResponse<{
          has_next: boolean;
          items: ICexAccount[];
        }>
      >(`/api/cex-pairs/${pairId}/accounts` + '?' + urlParams.toString());

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{
        has_next: boolean;
        items: ICexAccount[];
      }>({ error });
    }
  };

  public static getAccountById = async ({ account_id }: { account_id: number }) => {
    try {
      const response = await axiosToken.get<IBaseResponse<ICexAccountShort>>(
        `/api/accounts/${account_id}`,
      );

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{
        account: ICexAccountShort;
      }>({ error });
    }
  };

  public static getNotConnectedCexAccounts = async ({
    cexId,
    limit,
    lastSeenId,
  }: {
    cexId: number;
    limit: number;
    lastSeenId: number;
  }) => {
    try {
      const urlParams = new URLSearchParams();
      urlParams.append('limit', limit.toString());
      urlParams.append('last_seen_id', lastSeenId.toString());

      const response = await axiosToken.get<
        IBaseResponse<{
          has_next: boolean;
          items: ICexNotConnectedAccounts[];
        }>
      >(`/api/cex-pairs/${cexId}/accounts/not-connected` + '?' + urlParams.toString());

      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<{
        has_next: boolean;
        items: ICexNotConnectedAccounts[];
      }>({ error });
    }
  };

  public static connectAccountToCexPair = async ({
    cexId,
    accounts,
  }: {
    cexId: number;
    accounts: { bots_ids: number[]; id: number; is_disabled: boolean }[];
  }) => {
    try {
      const response = await axiosToken.put<IBaseResponse<any>>(
        `/api/cex-pairs/${cexId}/accounts`,
        {
          accounts,
        },
      );
      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };

  public static deleteAccountFromCexPair = async ({
    cexId,
    accounts_ids,
  }: {
    cexId: number;
    accounts_ids: number[];
  }) => {
    try {
      const response = await axiosToken.delete<IBaseResponse<any>>(
        `/api/cex-pairs/${cexId}/accounts`,
        {
          data: {
            accounts_ids: accounts_ids,
          },
        },
      );
      return mapResponse({
        data: response.data,
      });
    } catch (error) {
      return mapError<any>({ error });
    }
  };
}

export { ApiCexAccounts };
